// @flow

import * as React from "react";
import every from "lodash/every";
import compact from "lodash/compact";
import sum from "lodash/sum";
import { Field, FormSpy } from "react-final-form";
import type { FormSpyRenderProps } from "react-final-form";

import type { FormApi } from "final-form";
import ValidationSummary from "../../ValidationSummary";
import Table from "./Table";

import styles from "./FundingSourceTable.scss";
import type { SelectOption } from "../../Select/Select";
import { combineValidators, hasValue, required } from "../../../utils/validators";
import TableSummary from "./TableSummary";

type TableName = "purposeKind" | "productKind";

type FundingSourceTableProps = {
  form: FormApi,
  fieldNames: {
    sectionValid: string,
    purposeKind: {
      group: string,
      kind: string,
      value: string
    },
    productKind: {
      group: string,
      kind: string,
      value: string
    }
  },
  purposeKindOptions: Array<SelectOption>,
  productKindOptions: Array<SelectOption>
};

const tableNames = ["purposeKind", "productKind"];

class FundingSourceTable extends React.Component<FundingSourceTableProps> {
  componentDidUpdate() {
    this.updateSectionValidField();
  }

  updateSectionValidField = () => {
    const {
      form,
      fieldNames: { sectionValid: sectionValidFieldName }
    } = this.props;

    if (form) {
      const purposeKindSum = this.getSum("purposeKind");
      const productKindSum = this.getSum("productKind");
      const isPurposeKindValid = this.checkIfIsArrayFieldValid("purposeKind");
      const isProductKindValid = this.checkIfIsArrayFieldValid("productKind");

      const isSectionValid =
        isPurposeKindValid && isProductKindValid && purposeKindSum === productKindSum;

      form.change(sectionValidFieldName, isSectionValid);
    }
  };

  getTableConfigs = () => {
    const {
      fieldNames: { purposeKind, productKind },
      purposeKindOptions,
      productKindOptions
    } = this.props;

    return {
      purposeKind: {
        tableHeader: "Mittelverwendung",
        arrayFieldName: purposeKind.group,
        firstColumnHeader: "Objekt",
        firstColumnFieldName: purposeKind.kind,
        secondColumnFieldName: purposeKind.value,
        options: purposeKindOptions,
        summaryHeader: "Investitionssumme"
      },
      productKind: {
        tableHeader: "Mittelherkunft",
        arrayFieldName: productKind.group,
        firstColumnHeader: "Finanzierungsart",
        firstColumnFieldName: productKind.kind,
        secondColumnFieldName: productKind.value,
        options: productKindOptions,
        summaryHeader: "Finanzierungsstruktur"
      }
    };
  };

  renderTables = () => (
    <div className={styles.fundingTables}>
      {tableNames.map(tableName => this.renderTable(tableName))}
    </div>
  );

  renderTable = (tableName: TableName) => {
    const tableConfig = this.getTableConfigs()[tableName];

    return (
      <Table
        key={tableName}
        optionsToFilter={this.getOptionsToFilter(tableName)}
        tableConfig={tableConfig}
      />
    );
  };

  getArrayFieldState = (tableName: TableName) => {
    const { form } = this.props;
    const { arrayFieldName } = this.getTableConfigs()[tableName];

    return form && form.getFieldState(arrayFieldName);
  };

  checkIfIsArrayFieldValid = (tableName: TableName) => {
    const arrayFieldState = this.getArrayFieldState(tableName);
    return !!arrayFieldState && arrayFieldState.valid;
  };

  getOptionsToFilter = (tableName: TableName) => {
    const { firstColumnFieldName } = this.getTableConfigs()[tableName];
    const arrayFieldState = this.getArrayFieldState(tableName);

    if (!arrayFieldState) {
      return [];
    }

    const value = compact(arrayFieldState.value);

    return value && value.length ? value.map(row => row[firstColumnFieldName]) : [];
  };

  getValidationResults = () => {
    const fieldValidities = tableNames.map(tableName => this.checkIfIsArrayFieldValid(tableName));
    const purposeKindSum = this.getSum("purposeKind");
    const productKindSum = this.getSum("productKind");

    const getOption = (isValid, text) => [
      {
        isValid,
        text: () => text
      }
    ];

    if (every(fieldValidities) && purposeKindSum > 0 && productKindSum > 0) {
      if (purposeKindSum < productKindSum) {
        return getOption(
          false,
          "Die Investitionssumme ist geringer als die Summe der dargestellten Finanzierungsstruktur. Bitte korrigieren bzw. erweitern Sie die Mittelherkunft oder die Mittelverwendung."
        );
      }

      if (purposeKindSum > productKindSum) {
        return getOption(
          false,
          "Die Investitionssumme ist größer als die Summe der dargestellten Finanzierungsstruktur. Bitte korrigieren bzw. erweitern Sie die Mittelherkunft oder die Mittelverwendung."
        );
      }

      if ([purposeKindSum, productKindSum].every(amountSum => amountSum > 999999999)) {
        return getOption(false, "Der Investitionsbetrag darf nicht höher als 999.999.999 € sein");
      }

      return getOption(
        true,
        "Die Investitionssumme entspricht der Summe der dargestellten Finanzierungsstruktur."
      );
    }

    return [];
  };

  getSum = (tableName: TableName) => {
    const { form } = this.props;
    const { arrayFieldName, firstColumnFieldName, secondColumnFieldName } =
      this.getTableConfigs()[tableName];
    const arrayState = this.getArrayFieldState(tableName);

    const amounts =
      arrayState &&
      arrayState.value &&
      arrayState.value.map((value, valueIndex) => {
        if (!value) return 0;

        const fieldValidities = [firstColumnFieldName, secondColumnFieldName].map(fieldName => {
          const fieldState = form.getFieldState(`${arrayFieldName}[${valueIndex}].${fieldName}`);

          return !!fieldState && fieldState.valid;
        });
        const isValid = every(fieldValidities);

        return isValid ? parseInt(value[secondColumnFieldName], 10) : 0;
      });

    return amounts ? sum(amounts) : 0;
  };

  renderSectionValidField = () => {
    const {
      fieldNames: { sectionValid: sectionValidFieldName }
    } = this.props;

    return (
      <Field
        name={sectionValidFieldName}
        validate={combineValidators(required("error"), hasValue(true, "error"))}
        render={() => null}
      />
    );
  };

  renderValidatorResults = () => {
    const validationResults = this.getValidationResults();

    return (
      validationResults.length > 0 && (
        <div className={styles.validationSummaryContainer}>
          <ValidationSummary result={this.getValidationResults()} />
        </div>
      )
    );
  };

  renderTableSummaries = () => {
    const tableConfigs = this.getTableConfigs();

    return (
      <div className={styles.summaries}>
        {tableNames.map(tableName => (
          <TableSummary
            key={tableName}
            summaryHeader={tableConfigs[tableName].summaryHeader}
            sum={this.getSum(tableName).toLocaleString("de-DE")}
          />
        ))}
      </div>
    );
  };

  render() {
    return (
      <div>
        {this.renderSectionValidField()}
        {this.renderTables()}
        {this.renderTableSummaries()}
        {this.renderValidatorResults()}
      </div>
    );
  }
}

const FormSectionWrapper = (props: FundingSourceTableProps) => (
  <FormSpy
    render={({ form }: FormSpyRenderProps) => <FundingSourceTable {...props} form={form} />}
  />
);

FormSectionWrapper.defaultProps = {
  form: null
};

export default FormSectionWrapper;
